import { inject, Injectable, Injector } from '@angular/core';
import { forkJoin, map, of, switchMap, type Observable } from 'rxjs';

import { AuthorizationService } from '../../../api/services/authorization.service';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UserRolesStore } from '../../../features/admin/user-roles/user-roles.store';
import { UtilityService } from '../../services/utility.service';
import { debugLog } from '../../utils/debug-logger';
import type { RoleLike } from '../../utils/document-template-roles';
import {
  ONBOARDING_TEMPLATE_FILTER,
  ONBOARDING_TEMPLATE_MODULE_CODE,
  onboardingTargetRoleIds,
  templateMatchesTargetRoles,
} from '../../utils/onboarding-checklist-templates';
import { ReviewDocumentsQueryService } from './review-documents-query.service';

const ONBOARDING_FORMS_MODULE_CODE = 'onboarding-forms';
const TEMPLATE_PAGE_SIZE = 200;

/**
 * Resolves, for one aanmelding, the ids of the document templates its onboarding checklist shows —
 * so the reviewer's list and the approve/reject fan-out act on exactly the documents the carrier
 * was asked for. The filtering rule itself lives in `onboarding-checklist-templates.ts`.
 */
@Injectable({ providedIn: 'root' })
export class ReviewChecklistTemplatesService {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #documentsQuery = inject(ReviewDocumentsQueryService);
  // Resolved only when an onboarding form declares target roles: the role catalogue is an HTTP-backed
  // store, and most reviews (and every consumer's spec) never need it.
  readonly #injector = inject(Injector);

  /**
   * `onboardingFormRef` is the aanmelding's `entry['onboarding-form']`: either the full
   * onboarding-forms envelope or a thin `{ id, selectedInput }` reference, which is fetched.
   *
   * No template module means the checklist shows nothing either, so the set is empty rather than
   * an error — an error would block the review's Goedkeuren for a reason the carrier never saw.
   */
  visibleTemplateIds(onboardingFormRef: Entry | null | undefined): Observable<ReadonlySet<string>> {
    const templateModuleId = this.#modulesStore
      .enabledEntities()
      .find(module => module.code === ONBOARDING_TEMPLATE_MODULE_CODE)?.id;
    if (!templateModuleId) {
      debugLog('ReviewChecklistTemplates', 'template module not found', ONBOARDING_TEMPLATE_MODULE_CODE);
      return of(new Set<string>());
    }

    return forkJoin([
      this.#onboardingForm(onboardingFormRef),
      this.#documentsQuery.loadAll({
        moduleId: templateModuleId,
        filter: ONBOARDING_TEMPLATE_FILTER,
        pageSize: TEMPLATE_PAGE_SIZE,
      }),
    ]).pipe(
      switchMap(([form, templates]) => {
        const targetRoleIds = onboardingTargetRoleIds(form);
        return this.#roles(targetRoleIds.length > 0).pipe(
          map(roles => {
            const ids = templates
              .filter(template => templateMatchesTargetRoles(template, targetRoleIds, roles, UtilityService.createSlug))
              .map(template => template.id)
              .filter((id): id is string => typeof id === 'string' && !!id);
            return new Set(ids);
          })
        );
      })
    );
  }

  #onboardingForm(ref: Entry | null | undefined): Observable<Entry | null> {
    if (ref?.entry) return of(ref);
    const formId = ref?.id;
    const formsModuleId = this.#modulesStore.getModuleByCode(ONBOARDING_FORMS_MODULE_CODE)?.id;
    if (!formId || !formsModuleId) return of(null);
    return this.#entriesService.getEntry(formsModuleId, formId);
  }

  /** Role names/slugs are only needed to match slug-style `appliesToRoles`; skip the call otherwise. */
  #roles(needed: boolean): Observable<readonly RoleLike[]> {
    if (!needed) return of([]);
    const userRolesStore = this.#injector.get(UserRolesStore);
    if (userRolesStore.loaded()) return of(userRolesStore.userRoles());
    return this.#injector.get(AuthorizationService).getRoles();
  }
}
